import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Reply, MoreVertical, Edit, Trash2, Save, X, Flag } from 'lucide-react';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { formatDate } from '../../lib/utils/dateUtils';
import { ConversationReplyForm } from './ConversationReplyForm';
import { supabase } from '../../lib/supabase';
import { renderConversationContentWithMentions } from '../../lib/utils/mentionUtils';
import { ConversationRichContent } from './ConversationRichContent';
import { isVideoUrl } from '../../lib/utils/mediaUtils';
import { useAdmin } from '../../lib/hooks/useAdmin';
import type { ConversationComment as ConversationCommentType } from '../../types/conversations';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'sonner';

interface ConversationCommentProps {
  comment: ConversationCommentType;
  onReplyAdded: () => void;
  onCommentDeleted?: () => void;
  level?: number;
  isTopLevel?: boolean;
  showReplyButton?: boolean;
}

export function ConversationComment({
  comment,
  onReplyAdded,
  onCommentDeleted,
  level = 0,
  isTopLevel = true,
  showReplyButton = true
}: ConversationCommentProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [saving, setSaving] = useState(false);
  const [isOwnComment, setIsOwnComment] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const { isAdmin } = useAdmin();
  const menuRef = React.useRef<HTMLDivElement>(null);
  const { confirm, ConfirmDialog } = useConfirm();

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setIsOwnComment(user?.id === comment.user_id);
    });

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [comment.user_id]);

  const handleReplySuccess = () => {
    setShowReplyForm(false);
    onReplyAdded();
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditContent(comment.content);
    setShowMenu(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('conversation_comments')
        .update({
          content: editContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', comment.id);

      if (error) throw error;

      comment.content = editContent;
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComment = async () => {
    const confirmed = await confirm({
      title: "Delete Comment",
      description: "Are you sure you want to delete this comment? This action cannot be undone.",
      confirmText: "Delete",
      variant: "destructive"
    });

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('conversation_comments')
        .delete()
        .eq('id', comment.id);

      if (error) throw error;
      toast.success('Comment deleted successfully');
      onCommentDeleted?.();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!reportReason) {
      toast.error('Please select a reason for reporting');
      return;
    }
    try {
      setSubmittingReport(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to report');
        return;
      }
      const { error } = await supabase.from('reports').insert([{
        reporter_id: user.id,
        content_type: 'conversation_comment',
        content_id: comment.id,
        reason: reportReason,
        description: reportDescription || null
      }]);
      if (error) throw error;
      toast.success('Report submitted. Thank you for helping keep our community safe.');
      setShowReportModal(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  const canDelete = isAdmin || isOwnComment;
  const replyToCommentId = comment.parent_id || comment.id;
  const replyToUsername = comment.user?.username;

  return (
    <div className="flex space-x-3">
      <Link to={`/profile/${comment.user?.username}/listings`}>
        <Avatar
          url={comment.user?.avatar_url}
          size="sm"
          username={comment.user?.username}
          editable={false}
        />
      </Link>
      <div className="flex-1">
        {/* Row 1: Name, Date, Menu */}
        <div className="flex items-center justify-between mb-1">
          <Username
            username={comment.user?.username || 'Anonymous'}
            userId={comment.user_id}
            className="font-medium text-sm text-content"
          />
          <div className="flex items-center gap-2">
            <span className="text-xs text-content/60">
              {formatDate(new Date(comment.created_at))}
            </span>
            {canDelete && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                  disabled={isDeleting}
                >
                  <MoreVertical className="h-3 w-3" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg py-1 z-10 border border-gray-200">
                    {isOwnComment && (
                      <button
                        onClick={handleEdit}
                        className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 flex items-center transition-colors"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                    )}
                    <button
                      onClick={handleDeleteComment}
                      className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center transition-colors"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    {!isOwnComment && (
                      <button
                        onClick={() => { setShowReportModal(true); setShowMenu(false); }}
                        className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 flex items-center transition-colors"
                      >
                        <Flag className="h-3 w-3 mr-1" />
                        Report
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Content */}
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-2 text-sm border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
              rows={3}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 transition-colors flex items-center"
              >
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="px-3 py-1 text-xs bg-accent-text text-white rounded-md hover:bg-accent-text/90 transition-colors disabled:opacity-50 flex items-center"
                >
                  <Save className="h-3 w-3 mr-1" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {(comment.content || comment.images?.length) ? (
                <>
                  {comment.content ? (
                    <ConversationRichContent
                      className="text-base text-content/80"
                      html={renderConversationContentWithMentions(comment.content)}
                    />
                  ) : null}
                  {comment.images && comment.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {comment.images.map((url, i) =>
                        isVideoUrl(url) ? (
                          <video
                            key={i}
                            src={url}
                            className="max-h-40 max-w-xs rounded-lg object-cover"
                            controls
                            muted
                            loop
                            playsInline
                          />
                        ) : (
                          <img
                            key={i}
                            src={url}
                            alt=""
                            className="max-h-40 rounded-lg object-cover"
                          />
                        )
                      )}
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}

        <div className="mt-1">
          {showReplyButton ? (
            <>
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="text-xs text-content/60 hover:text-accent-text flex items-center"
              >
                <Reply className="h-3 w-3 mr-1" />
                Reply
              </button>
              {showReplyForm && (
                <div className="mt-2">
                  <ConversationReplyForm
                    parentId={replyToCommentId}
                    postId={comment.post_id}
                    replyToUsername={replyToUsername}
                    replyToUserId={comment.user_id}
                    onSuccess={handleReplySuccess}
                    onCancel={() => setShowReplyForm(false)}
                  />
                </div>
              )}
            </>
          ) : (
            <button
              onClick={() => {
                sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
                window.dispatchEvent(new CustomEvent('show-auth'));
              }}
              className="text-xs text-[#F49F0D] hover:text-[#F49F0D]/80 flex items-center"
            >
              <Reply className="h-3 w-3 mr-1" />
              Log in to reply
            </button>
          )}
        </div>

        {isTopLevel && comment.replies && comment.replies.length > 0 && (
          <div className="mt-3 space-y-3 pl-3 border-l-2 border-accent-base">
            {comment.replies.map((reply) => (
              <ConversationComment
                key={reply.id}
                comment={reply}
                onReplyAdded={onReplyAdded}
                onCommentDeleted={onCommentDeleted}
                level={1}
                isTopLevel={false}
              />
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog />

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowReportModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-content">Report Comment</h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Why are you reporting this comment?</p>
            <div className="space-y-2 mb-4">
              {[
                { value: 'spam', label: 'Spam' },
                { value: 'inappropriate', label: 'Inappropriate content' },
                { value: 'harassment', label: 'Harassment or bullying' },
                { value: 'other', label: 'Other' }
              ].map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    reportReason === reason.value
                      ? 'border-accent-text bg-accent-text/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="reportReason"
                    value={reason.value}
                    checked={reportReason === reason.value}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-sm text-content">{reason.label}</span>
                </label>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional details (optional)
              </label>
              <textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Provide more context..."
                className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReport}
                disabled={!reportReason || submittingReport}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReport ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
